// Model fog classifier for safety policy v2 (accelerated rollout Phase E, 1 October 2026;
// docs/handover/phase-2c.md, "Accelerated rollout, Phase E (1 October 2026)", gates addendum A16).
// One rule for the visibility (fog) row of every v2 safety module that carries one: surf and
// bodyboarding, kite and windsurf, wing foil, prone foil, downwind foil, the stand-up paddle
// profiles (Touring and the legacy product, Surf, Downwind), diving, swim and sailing, in the
// engines' per-hour modules, the named paddle transport and the interim day summary the surf slice
// publishes at S2.
//
// Why: every row read Open-Meteo's modelled visibility alone, red below 500 m and amber below
// 1,000 m. Against the nearest METAR station (the public IEM ASOS archive; 133 stations within 40
// km of 178 coastal spots and 34 sailing venues; every cached daylight hour with a modelled
// visibility, 5 May to 29 September 2026, the recorded era only, since archive rows carry no
// visibility: one austral winter and one boreal summer, no boreal winter) the red hour had fog
// (under 1,000 m, or FG reported) at the station 13.5% of the time (121 of 899 hours) and 5 km or
// more 78.5% of the time. Fog needs saturated air, and a modelled sub-500 m hour whose air is not
// saturated is the visibility diagnostic disagreeing with the model's own moisture. Measured on the
// same hours, the share with fog at the station is about 20% at a relative humidity of 94% or more,
// 8% from 88% to 93% and 2.8% below 88%. Calm wind, very low modelled visibility and the morning
// separate genuine fog in every station group too; saturation was chosen for its physical basis and
// because it keeps the most recall, and a calm-wind condition was turned down because it drops the
// windy advection sea fog seen at Cape Town, Mavericks and Reykjanes (3 events, 6 hours; gates
// addendum A16, which also records it as an option for sailing, where no windy red hour had fog).
//
// Humidity does not separate fog well everywhere: in the advection marine layer of the US west
// coast (HAF, WVI, TMK, AST, SFO, OAK, NKX) sub-500 m hours had fog on 8.5% of hours below 88%,
// 7.3% from 88% to 93% and 11.6% at 94% or more, and dense fog the station saw (Mavericks, Santa
// Cruz and Pacific City, 28 and 29 September 2026) came in modelled air of 64% to 85%. So an
// unconfirmed hour is never dropped: it keeps an amber row ("Reduced visibility"), and only the
// red needs the model's own saturation.
//
// The saturation test reads the hour's relative humidity, the provider's own saturation measure
// (its dew point is derived from the humidity and the temperature): 94% is a dew-point depression
// of about 1 C (0.96 C at 15 C; the depression at a given humidity moves by about 0.1 C per 15 C of
// temperature), and on the 1,299 paired low-visibility hours the two tests agree on 1,294. The
// humidity has no presence bit (the normalisers store a provider null as null), so a recorded row
// from before the optional weather bits, whose temperature cannot be proven, is judged as a live
// one, and the lean issue frames carry it (engines/live/lean-vars.ts).
//
// The rule (MODEL_FOG_V2). Each sport keeps its revision 1 visibility thresholds: amber below
// 1,000 m, red below 500 m (the stand-up paddle Touring and legacy rows and swim: 300 m).
// - unavailable: the visibility is missing (never clear);
// - no row: the visibility is 1,000 m or more;
// - red: the visibility is below the sport's red threshold and the relative humidity is 94% or
//   more, or unknown (an unknown humidity never lowers a level);
// - amber: every other visibility below 1,000 m ("Reduced visibility": modelled fog the air does
//   not confirm, at any humidity, or a visibility between the red threshold and 1,000 m).
// The rule never removes a row the visibility-alone rule showed: it only lowers unconfirmed reds
// to amber.
//
// Pure and dependency-free: the route's interim rows (forecast-safety.ts, a declaration-only
// importer of the engines), the sailing leaf and the engines' safety modules import it alike.

export const MODEL_FOG_V2 = Object.freeze({
  /** No row at or above this visibility (m). */
  amberBelowM: 1000,
  /** Red below this visibility (m) when the air is saturated (the default; a sport may pass its own). */
  redBelowM: 500,
  /** Saturated: a relative humidity at or above this (%), a dew-point depression of about 1 C. */
  saturatedFromHumidityPct: 94,
});

/** The level the visibility row takes: null is no row. */
export type ModelFogLevel = "red" | "amber" | "unavailable" | null;

export type ModelFogReading = Readonly<{
  /** Modelled visibility (m); null when missing. */
  visibilityM: number | null;
  /**
   * The hour's relative humidity (%); null or omitted when unknown, which never lowers the level
   * (the air counts as saturated).
   */
  relativeHumidityPct?: number | null;
}>;

const finite = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

/** A stored relative humidity (%) as the fog rule reads it: finite and from 0 to 100, else null. */
export function modelFogHumidityPct(value: number | null | undefined): number | null {
  return finite(value) && value >= 0 && value <= 100 ? value : null;
}

/**
 * The model fog level of one reading (see the header). `redBelowM` is the sport's revision 1 red
 * visibility (MODEL_FOG_V2.redBelowM unless the sport's was lower).
 */
export function classifyModelFog(reading: ModelFogReading, redBelowM: number = MODEL_FOG_V2.redBelowM): ModelFogLevel {
  const { visibilityM } = reading;
  if (!finite(visibilityM)) return "unavailable";
  if (visibilityM >= MODEL_FOG_V2.amberBelowM) return null;
  const humidity = modelFogHumidityPct(reading.relativeHumidityPct);
  const saturated = humidity === null || humidity >= MODEL_FOG_V2.saturatedFromHumidityPct;
  return visibilityM < redBelowM && saturated ? "red" : "amber";
}

const LEVEL_RANK: Readonly<Record<"red" | "amber" | "none", number>> = Object.freeze({ red: 2, amber: 1, none: 0 });

/**
 * The reading that governs a day's single visibility row (the interim day summary): the worst level
 * over the readings at the given red threshold, the lowest visibility among equals; readings with a
 * missing visibility are skipped. Null when no reading has a visibility.
 */
export function governingModelFogReading(readings: readonly ModelFogReading[], redBelowM: number = MODEL_FOG_V2.redBelowM): ModelFogReading | null {
  let best: ModelFogReading | null = null;
  let bestRank = -1;
  for (const reading of readings) {
    if (!finite(reading.visibilityM)) continue;
    const level = classifyModelFog(reading, redBelowM);
    const rank = LEVEL_RANK[level === "red" || level === "amber" ? level : "none"];
    if (best === null || rank > bestRank || (rank === bestRank && reading.visibilityM < (best.visibilityM as number))) {
      best = reading;
      bestRank = rank;
    }
  }
  return best;
}
